import type { Stay, Tier } from '@dia/db';

/**
 * Manual stay entry (Section 5, Phase 1). Pure helpers: the code alphabet, the
 * form check, and the status a stay should have on a given day.
 */

/** The same alphabet the guest-login function checks: no I, O, 0 or 1. */
export const STAY_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';

export function generateStayCode(bytes: (n: number) => Uint8Array = randomBytes): string {
  let out = '';
  const limit = 256 - (256 % STAY_CODE_ALPHABET.length);
  while (out.length < 8) {
    for (const byte of bytes(16)) {
      if (byte >= limit) continue;
      out += STAY_CODE_ALPHABET[byte % STAY_CODE_ALPHABET.length];
      if (out.length === 8) break;
    }
  }
  return out;
}

function randomBytes(n: number): Uint8Array {
  const buf = new Uint8Array(n);
  globalThis.crypto.getRandomValues(buf);
  return buf;
}

export type StayForm = {
  tier: Tier;
  arrives_on: string;
  departs_on: string;
  adults: number;
  children: number;
  arrival_flight: string;
  arrival_boat_time: string;
  dietary_notes: string;
  notes_internal: string;
  lead_first_name: string;
  lead_last_name: string;
};

export type StayProblem = 'dates' | 'people' | 'lead_name' | 'missing_dates';

export function validateStay(form: StayForm, editing: boolean): StayProblem[] {
  const problems: StayProblem[] = [];
  if (!form.arrives_on || !form.departs_on) problems.push('missing_dates');
  else if (form.departs_on < form.arrives_on) problems.push('dates');
  if (form.adults + form.children < 1 || form.adults < 0 || form.children < 0) problems.push('people');
  // The last name is what a guest signs in with, so a new stay cannot skip it.
  if (!editing && !form.lead_last_name.trim()) problems.push('lead_name');
  return problems;
}

/** The status a stay has on a given day, from its dates. Cancelled stays stay cancelled. */
export function statusForDay(
  stay: Pick<Stay, 'arrives_on' | 'departs_on' | 'status'>,
  today: string,
): Stay['status'] {
  if (stay.status === 'cancelled') return 'cancelled';
  if (today < stay.arrives_on) return 'upcoming';
  if (today > stay.departs_on) return 'departed';
  return 'in_house';
}

export type Island = {
  inHouse: { people: number; adults: number; children: number; departsOn: string | null };
  arrivals: number;
  departures: number;
};

/** What Hoy says about who is on the island. Works from the crew view of a stay. */
export function islandToday(
  stays: Pick<Stay, 'arrives_on' | 'departs_on' | 'status' | 'adults' | 'children'>[],
  today: string,
): Island {
  const live = stays.filter((s) => s.status !== 'cancelled');
  const here = live.filter((s) => s.arrives_on <= today && today <= s.departs_on);
  return {
    inHouse: {
      people: here.reduce((n, s) => n + s.adults + s.children, 0),
      adults: here.reduce((n, s) => n + s.adults, 0),
      children: here.reduce((n, s) => n + s.children, 0),
      departsOn: here.map((s) => s.departs_on).sort()[0] ?? null,
    },
    arrivals: live.filter((s) => s.arrives_on === today).length,
    departures: live.filter((s) => s.departs_on === today).length,
  };
}
